import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate } from "@/pages/admin/util";
import { Loader2, RefreshCw, Plus, Pencil, Trash2, Building2, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  slug: "",
  headquarters: "",
  year_founded: "",
  parks_count_estimate: "",
  regions_covered: "",
  website: "",
  summary: "",
  key_facts: "",
  status: "PUBLISHED",
};

function toForm(op) {
  if (!op) return { ...EMPTY };
  return {
    name: op.name || "",
    slug: op.slug || "",
    headquarters: op.headquarters || "",
    year_founded: op.year_founded ?? "",
    parks_count_estimate: op.parks_count_estimate ?? "",
    regions_covered: (op.regions_covered || []).join(", "),
    website: op.website || "",
    summary: op.summary || "",
    key_facts: (op.key_facts || []).join("\n"),
    status: op.status || "PUBLISHED",
  };
}

function toPayload(form) {
  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    headquarters: form.headquarters.trim(),
    year_founded: form.year_founded === "" ? null : Number(form.year_founded),
    parks_count_estimate: form.parks_count_estimate === "" ? null : Number(form.parks_count_estimate),
    regions_covered: form.regions_covered.split(",").map((s) => s.trim()).filter(Boolean),
    website: form.website.trim(),
    summary: form.summary.trim(),
    key_facts: form.key_facts.split("\n").map((s) => s.trim()).filter(Boolean),
    status: form.status,
  };
}

export default function AdminIntelligenceOperators() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | operator obj
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/park-operators");
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load operators");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function remove(op) {
    if (!confirm(`Delete operator "${op.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/park-operators/${op.id}`);
      toast.success("Operator deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  async function seed() {
    setSeeding(true);
    try {
      const { data } = await adminApi.post("/intelligence/seed");
      toast.success(`Seeded ${data.operators_seeded} operators, ${data.repair_costs_seeded} cost benchmarks`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Seed failed");
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-intel-operators-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">Intelligence platform</p>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Park Operators</h1>
          <p className="text-sm text-[#475569] mt-1 max-w-2xl">Real, verifiable operators only. We seed the 4 majors — add new entries only when you can cite a public source.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={seed} disabled={seeding} className="btn-outline h-10 py-0 px-4 text-sm" data-testid="seed-intelligence-btn">
            {seeding ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Seed majors
          </button>
          <button onClick={load} className="btn-outline h-10 py-0 px-4 text-sm" data-testid="operators-refresh-btn">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => setEditing("new")} className="btn-primary h-10 py-0 px-4 text-sm" data-testid="operators-new-btn">
            <Plus size={14} /> New operator
          </button>
        </div>
      </div>

      <div className="card-default p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.18em]">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">HQ</th>
              <th className="px-4 py-3">Parks (est.)</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2DCD0]">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#475569]"><Loader2 className="inline animate-spin" size={16}/></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#475569]">No operators yet. Click <strong>Seed majors</strong> to load the 4 known operators.</td></tr>
            )}
            {!loading && items.map((op) => (
              <tr key={op.id} data-testid={`operator-row-${op.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#071A33] flex items-center gap-2"><Building2 size={14} className="text-[#064E2E]"/>{op.name}</div>
                  <div className="text-xs text-[#94A3B8]">/{op.slug}</div>
                </td>
                <td className="px-4 py-3 text-[#334155]">{op.headquarters || "—"}</td>
                <td className="px-4 py-3 tabular-nums">{op.parks_count_estimate ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-[10px] tracking-[0.1em] uppercase ${op.status === "PUBLISHED" ? "bg-[#064E2E]/10 text-[#064E2E]" : "bg-[#475569]/15 text-[#475569]"}`}>{op.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-[#475569]">{fmtDate(op.updated_at, true)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    {op.website && (
                      <a href={op.website} target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-[#F7F4ED]" title="Visit website">
                        <ExternalLink size={14} className="text-[#475569]"/>
                      </a>
                    )}
                    <button onClick={() => setEditing(op)} className="p-2 rounded-full hover:bg-[#F7F4ED]" title="Edit" data-testid={`operator-edit-${op.slug}`}>
                      <Pencil size={14} className="text-[#0F766E]"/>
                    </button>
                    <button onClick={() => remove(op)} className="p-2 rounded-full hover:bg-[#FCA5A5]/20" title="Delete">
                      <Trash2 size={14} className="text-[#DC2626]"/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <OperatorDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function OperatorDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(toForm(initial));
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = toPayload(form);
      if (initial) {
        await adminApi.put(`/park-operators/${initial.id}`, payload);
        toast.success("Operator updated");
      } else {
        await adminApi.post("/park-operators", payload);
        toast.success("Operator created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-4 overflow-y-auto" data-testid="operator-dialog">
      <form onSubmit={submit} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl my-6">
        <div className="px-6 py-5 border-b border-[#E2DCD0] flex items-center gap-3">
          <Building2 size={20} className="text-[#064E2E]"/>
          <h2 className="font-display text-2xl text-[#071A33]">{initial ? "Edit operator" : "New operator"}</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Operator name *</label>
              <input className="field-input" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="op-name"/>
            </div>
            <div>
              <label className="field-label">Slug <span className="text-[#94A3B8] font-normal">(auto if blank)</span></label>
              <input className="field-input" value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="auto-generated"/>
            </div>
            <div>
              <label className="field-label">Headquarters</label>
              <input className="field-input" value={form.headquarters} onChange={(e) => update("headquarters", e.target.value)} placeholder="e.g. Brentwood, Essex"/>
            </div>
            <div>
              <label className="field-label">Website</label>
              <input className="field-input" type="url" value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://"/>
            </div>
            <div>
              <label className="field-label">Year founded</label>
              <input className="field-input" type="number" min="1800" max="2100" value={form.year_founded} onChange={(e) => update("year_founded", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Parks count (estimate)</label>
              <input className="field-input" type="number" min="0" value={form.parks_count_estimate} onChange={(e) => update("parks_count_estimate", e.target.value)}/>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Regions covered <span className="text-[#94A3B8] font-normal">(comma-separated)</span></label>
              <input className="field-input" value={form.regions_covered} onChange={(e) => update("regions_covered", e.target.value)} placeholder="England, Scotland, Wales"/>
            </div>
          </div>
          <div>
            <label className="field-label">Summary <span className="text-[#94A3B8] font-normal">(public description)</span></label>
            <textarea className="field-input min-h-[100px]" value={form.summary} onChange={(e) => update("summary", e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Key facts <span className="text-[#94A3B8] font-normal">(one per line)</span></label>
            <textarea className="field-input min-h-[90px]" value={form.key_facts} onChange={(e) => update("key_facts", e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Status</label>
            <select className="field-input" value={form.status} onChange={(e) => update("status", e.target.value)}>
              <option value="PUBLISHED">Published</option>
              <option value="DRAFT">Draft</option>
            </select>
          </div>
        </div>
        <div className="px-6 py-4 bg-[#F7F4ED] border-t border-[#E2DCD0] flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} className="btn-outline" disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="operator-save-btn">
            {busy ? <><Loader2 className="animate-spin" size={16}/> Saving…</> : initial ? "Save changes" : "Create operator"}
          </button>
        </div>
      </form>
    </div>
  );
}
